import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send, Loader2 } from "lucide-react";

// Si REACT_APP_BACKEND_URL n'est pas défini au build, on retombe sur la même
// origine que le site (le reverse-proxy route /api vers le backend FastAPI).
// Sans ce fallback, l'URL devenait littéralement "undefined/api/leads" -> 404.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const PROFILES = [
  { id: "agence", label: "Agence Immobilière", sub: "Je gère des biens pour des propriétaires" },
  { id: "gestionnaire", label: "Gestionnaire", sub: "Je gère un portefeuille de biens" },
  { id: "proprietaire", label: "Propriétaire", sub: "Je possède plusieurs biens locatifs" },
  { id: "syndic", label: "Syndic / SCI", sub: "Copropriété ou société civile immobilière" },
];

const PROPERTY_COUNT_OPTIONS = [
  "1-10 biens", "11-50 biens", "51-100 biens", "101-200 biens", "200+ biens",
];

// FastAPI renvoie `detail` sous forme de tableau pour les erreurs de validation
// (422) : sans normalisation le toast affichait "[object Object]".
const extractErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msg = detail
      .map((d) => (typeof d === "string" ? d : d?.msg))
      .filter(Boolean)
      .join(" — ");
    if (msg) return msg;
  }
  return "Une erreur est survenue. Réessayez ou écrivez-nous à contact@mikassa.app.";
};

const ContactForm = () => {
  const [profile, setProfile] = useState("agence");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    property_count: "",
    current_tool: "",
    message: "",
  });

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Nom et email requis");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        profile,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        organization: form.organization || null,
        property_count: form.property_count || null,
        current_tool: form.current_tool || null,
        message: form.message || null,
      };
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
      toast.success("Votre demande a été transmise. Un conseiller vous recontactera.");
    } catch (err) {
      console.error(err);
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0C2340] text-white py-24 md:py-40 overflow-hidden"
    >
      {/* Diamond decor */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rotate-45 border border-white/8 pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] rotate-45 border border-[#E8611A]/15 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-16">
        {/* <div className="flex items-center gap-3 mb-8">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-white/60">
            Demandez votre démo
          </span>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <h2
            data-testid="contact-title"
            className="lg:col-span-8 font-heading font-black text-white text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Prêt à simplifier<br />
            <span className="text-[#E8611A]">votre gestion ?</span>
          </h2>
          {/* <p className="lg:col-span-4 font-body text-white/70 text-base md:text-lg leading-relaxed">
            Demandez une démo personnalisée. Nous vous montrerons comment
            Mikassa  peut transformer votre activité.
          </p> */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Profile switcher */}
          <div className="lg:col-span-4 flex flex-col gap-2">
            {PROFILES.map((p) => (
              <button
                key={p.id}
                onClick={() => setProfile(p.id)}
                data-testid={`profile-${p.id}`}
                className={`text-left border-l-2 px-5 py-4 transition-all duration-500 ${
                  profile === p.id
                    ? "border-[#E8611A] bg-white/[0.04]"
                    : "border-white/15 hover:border-white/40"
                }`}
              >
                <div className={`font-heading font-black text-xl md:text-2xl tracking-tight ${
                  profile === p.id ? "text-white" : "text-white/60"
                }`}>
                  {p.label}
                </div>
                <div className="font-body text-white/50 text-xs md:text-sm mt-1">
                  {p.sub}
                </div>
              </button>
            ))}

            <div className="mt-8 space-y-3 text-white/70">
              <div className="opacity-40">
                <div className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50 mb-1">Adresse</div>
                <div className="font-body text-sm">RCI - Abidjan, Cocody 2 Plateaux</div>
              </div>
              <div>
                <div className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50 mb-1">Email</div>
                <div className="font-body text-sm">
                  <a href="mailto:contact@mikassa.app" className="hover:text-[#E8611A] transition-colors">contact@mikassa.app</a>
                </div>
              </div>
              <div>
                <div className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/50 mb-1">Téléphone</div>
                <div className="font-body text-sm">
                  <a href="tel:+2250595476593" className="hover:text-[#E8611A] transition-colors">+225 05 95 476 593</a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8 bg-[#0A1C33] p-8 md:p-12 border border-white/10">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="flex flex-col items-start justify-center min-h-[400px] gap-4"
              >
                <CheckCircle2 size={48} className="text-[#E8611A]" />
                <h3 className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
                  Merci, {form.full_name.split(" ")[0]}.
                </h3>
                <p className="font-body text-white/70 max-w-md">
                  Votre demande de démo a bien été enregistrée. Un conseiller
                  Mikassa vous contactera sous 24 h pour organiser votre démonstration.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ full_name: "", email: "", phone: "", organization: "", property_count: "", current_tool: "", message: "" });
                  }}
                  className="mt-6 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#E8611A] hover:underline"
                >
                  Envoyer une autre demande →
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Nom complet *</label>
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={handleChange("full_name")}
                    data-testid="input-full-name"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                    placeholder="Ex. Aminata Koné"
                  />
                </div>
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange("email")}
                    data-testid="input-email"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                    placeholder="votre@email.com"
                  />
                </div>
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    data-testid="input-phone"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                    placeholder="+225 …"
                  />
                </div>

                {(profile === "agence" || profile === "syndic") && (
                  <div>
                    <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">
                      Nom de l'organisation
                    </label>
                    <input
                      type="text"
                      value={form.organization}
                      onChange={handleChange("organization")}
                      data-testid="input-organization"
                      className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors"
                      placeholder="Nom de votre agence ou structure"
                    />
                  </div>
                )}

                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Nombre de biens gérés</label>
                  <select
                    value={form.property_count}
                    onChange={handleChange("property_count")}
                    data-testid="input-property-count"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white focus:outline-none focus:border-[#E8611A] transition-colors"
                  >
                    <option value="" className="bg-[#0A1C33]">— Sélectionnez —</option>
                    {PROPERTY_COUNT_OPTIONS.map((p) => (
                      <option key={p} value={p} className="bg-[#0A1C33]">{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Outil actuel</label>
                  <select
                    value={form.current_tool}
                    onChange={handleChange("current_tool")}
                    data-testid="input-current-tool"
                    className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white focus:outline-none focus:border-[#E8611A] transition-colors"
                  >
                    <option value="" className="bg-[#0A1C33]">— Comment gérez-vous actuellement ? —</option>
                    <option value="excel" className="bg-[#0A1C33]">Excel / Tableur</option>
                    <option value="papier" className="bg-[#0A1C33]">Papier / Cahiers</option>
                    <option value="autre-logiciel" className="bg-[#0A1C33]">Autre logiciel</option>
                    <option value="rien" className="bg-[#0A1C33]">Pas d'outil particulier</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-white/50 block mb-2">Message</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={handleChange("message")}
                  data-testid="input-message"
                  className="w-full bg-transparent border-b-2 border-white/15 py-3 px-0 font-body text-white placeholder-white/30 focus:outline-none focus:border-[#E8611A] transition-colors resize-none"
                  placeholder="Dites-nous en plus sur votre projet…"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
                <p className="font-body text-white/40 text-xs max-w-md">
                  En soumettant ce formulaire, vous acceptez d'être contacté par Mikassa Agency. Vos données ne seront jamais partagées avec des tiers.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="mikassa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Envoi…
                    </>
                  ) : (
                    <>
                      Envoyer ma demande <Send size={14} />
                    </>
                  )}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
